import React from 'react';
import { Link } from 'react-router-dom';

const ApiReference = () => {
  const providers = [
    {
      name: 'DeepSeek',
      description: 'Low-cost, high-performance AI model',
      link: 'https://platform.deepseek.com',
      category: 'Budget'
    },
    {
      name: 'OpenAI',
      description: 'GPT-4, GPT-3.5, and other models',
      link: 'https://platform.openai.com',
      category: 'Premium'
    },
    {
      name: 'Anthropic',
      description: 'Claude models for advanced reasoning',
      link: 'https://console.anthropic.com',
      category: 'Premium'
    },
    {
      name: 'Google Gemini',
      description: 'Google\'s advanced AI models',
      link: 'https://ai.google.dev',
      category: 'Premium'
    },
    {
      name: 'Groq',
      description: 'Ultra-fast inference API',
      link: 'https://console.groq.com',
      category: 'Fast & Budget'
    },
    {
      name: 'Together AI',
      description: 'Open source and custom models',
      link: 'https://together.ai',
      category: 'Fast & Budget'
    },
    {
      name: 'Mistral AI',
      description: 'High-quality open models',
      link: 'https://console.mistral.ai',
      category: 'Quality'
    },
    {
      name: 'Cohere',
      description: 'Enterprise-grade language models',
      link: 'https://cohere.com',
      category: 'Quality'
    },
    {
      name: 'OpenRouter',
      description: 'Access to 100+ AI models',
      link: 'https://openrouter.ai',
      category: 'Quality'
    },
    {
      name: 'Ollama',
      description: 'Run models locally on your machine',
      link: 'https://ollama.ai',
      category: 'Local / Free'
    }
  ];

  const categories = ['Local / Free', 'Fast & Budget', 'Quality', 'Premium'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent tracking-tight">
            API Reference
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 font-light tracking-wide">
            Configure AI providers and manage API keys
          </p>
        </div>

        {/* Virtual Key Service */}
        <div className="premium-card p-8 mb-12 animate-slide-up bg-gradient-to-br from-primary/10 to-purple-600/10 border-primary/20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Virtual API Key Service</h2>
              <p className="text-gray-600 dark:text-gray-400">Coming Soon</p>
            </div>
          </div>
          <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
            We're building a virtual API key service that will allow you to use AI providers without managing individual API keys. 
            This service will work across all our applications and provide a unified interface for AI access.
          </p>
          <a 
            href="https://virtualkeys.example.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-purple-600 hover:from-primary-dark hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
          >
            Learn More
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>

        {/* AI Providers */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 tracking-tight">Supported AI Providers</h2>
          
          {categories.map((category) => {
            const categoryProviders = providers.filter(p => p.category === category);
            if (categoryProviders.length === 0) return null;
            
            return (
              <div key={category} className="mb-10">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                  <span className="w-1 h-6 bg-gradient-to-b from-primary to-purple-600 rounded-full"></span>
                  {category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryProviders.map((provider) => (
                    <div 
                      key={provider.name}
                      className="premium-card p-6 hover:scale-[1.02] transition-transform duration-200"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                          {provider.name}
                        </h4>
                        <a
                          href={provider.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary-dark transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {provider.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Setup Instructions */}
        <div className="premium-card p-8 mt-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Getting Started</h2>
          <div className="space-y-4 text-gray-700 dark:text-gray-300">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-primary font-bold">1</span>
              </div>
              <div>
                <p className="font-semibold mb-1">Choose a Provider</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Select an AI provider from the list above that fits your needs and budget.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-primary font-bold">2</span>
              </div>
              <div>
                <p className="font-semibold mb-1">Get Your API Key</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Sign up with the provider and obtain your API key from their dashboard.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-primary font-bold">3</span>
              </div>
              <div>
                <p className="font-semibold mb-1">Configure in Settings</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Go to Settings → API Keys Configuration and enter your API key.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary-light font-medium transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to App
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ApiReference;
